import { useAuth } from "@clerk/react";
import { useQuery } from "@tanstack/react-query";
import { authenticatedApi } from "@/lib/api";
import type { FeaturesResponse } from "../../../shared/api-types";

export {
  ALWAYS_BRAND_FLAG,
  NOTIFICATIONS_FLAG,
  PATIENT_DETAIL_FLAG,
  PATIENT_MESSAGING_FLAG,
  RD_HOME_FLAG,
  RD_SCHEDULE_FLAG,
  SESSION_PATIENT_LINKS_FLAG,
  SOCIAL_NOTES_FLAG,
} from "../../../shared/features";

/**
 * Server-evaluated feature-flag state for the SPA. Shared query key so every
 * useFeatureEnabled call site dedupes to one fetch.
 */
export function useFeatures() {
  const { getToken, isLoaded, isSignedIn } = useAuth({
    treatPendingAsSignedOut: false,
  });

  return useQuery({
    queryKey: ["features"],
    // Same session gate as the /api/me query in WorkspaceShell: a signed-out
    // visitor must not fire /api/features, take two 401s, and spend the
    // once-per-page-load sign-out guard before RedirectToSignIn runs.
    enabled: isLoaded && isSignedIn,
    queryFn: () =>
      authenticatedApi<{ data: FeaturesResponse }>(getToken, "/api/features"),
  });
}

/** True only when the server evaluated the flag on. Loading/error read as off. */
export function useFeatureEnabled(flag: string): boolean {
  const features = useFeatures();

  return features.data?.data[flag] === true;
}
